import type { ReactNode } from "react";
import {
  Eye,
  Gauge,
  Globe,
  MoreHorizontal,
  Pause,
  Play,
  Store,
  Trash2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type WorkspaceActionsTenant = {
  id: string;
  name: string;
  slug: string;
  lifecycleStatus?: string;
  subscriptionStatus?: string | null;
  isDemo?: boolean;
};

export type WorkspaceActionsMenuProps = {
  tenant: WorkspaceActionsTenant;
  publicStoreHref: string;
  onImpersonate: () => void;
  onProvisionSubdomain: () => void;
  onCustomDomain: () => void;
  onLimitsOverride?: () => void;
  onSetLifecycle: (status: "active" | "paused" | "suspended" | "pending_approval") => void | Promise<void>;
  onSetSubscriptionStatus: (status: "active" | "suspended") => void | Promise<void>;
  onDelete?: () => void;
  demoReset?: { label: string; onClick: () => void };
  demoReseed?: { label: string; onClick: () => void };
  trigger?: ReactNode;
};

function lifecycleOf(status?: string) {
  return status?.trim() || "active";
}

/** Menú ⋯ de acciones por workspace — paridad Booking Hub ↔ Food Market. */
export function WorkspaceActionsMenu({
  tenant,
  publicStoreHref,
  onImpersonate,
  onProvisionSubdomain,
  onCustomDomain,
  onLimitsOverride,
  onSetLifecycle,
  onSetSubscriptionStatus,
  onDelete,
  demoReset,
  demoReseed,
  trigger,
}: WorkspaceActionsMenuProps) {
  const lifecycle = lifecycleOf(tenant.lifecycleStatus);
  const subStatus = tenant.subscriptionStatus ?? "active";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={onImpersonate}>
          <Eye className="mr-2 h-4 w-4" /> Impersonar
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={publicStoreHref} target="_blank" rel="noreferrer">
            <Store className="mr-2 h-4 w-4" /> Ver tienda pública
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onProvisionSubdomain}>
          <Globe className="mr-2 h-4 w-4" /> Crear subdominio DNS
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onCustomDomain}>
          <Globe className="mr-2 h-4 w-4" /> Dominio propio
        </DropdownMenuItem>
        {onLimitsOverride ? (
          <DropdownMenuItem onClick={onLimitsOverride}>
            <Gauge className="mr-2 h-4 w-4" /> Límites exclusivos
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator />

        {lifecycle === "pending_approval" ? (
          <DropdownMenuItem onClick={() => void onSetLifecycle("active")}>
            <Play className="mr-2 h-3.5 w-3.5 text-emerald-400" /> Activar workspace
          </DropdownMenuItem>
        ) : null}
        {lifecycle === "active" ? (
          <DropdownMenuItem onClick={() => void onSetLifecycle("paused")}>
            <Pause className="mr-2 h-3.5 w-3.5 text-amber-400" /> Pausar workspace
          </DropdownMenuItem>
        ) : null}
        {lifecycle === "paused" ? (
          <DropdownMenuItem onClick={() => void onSetLifecycle("active")}>
            <Play className="mr-2 h-3.5 w-3.5 text-emerald-400" /> Reanudar workspace
          </DropdownMenuItem>
        ) : null}
        {lifecycle === "suspended" ? (
          <DropdownMenuItem onClick={() => void onSetLifecycle("active")}>
            <Play className="mr-2 h-3.5 w-3.5 text-emerald-400" /> Levantar suspensión
          </DropdownMenuItem>
        ) : null}

        {subStatus !== "active" ? (
          <DropdownMenuItem onClick={() => void onSetSubscriptionStatus("active")}>
            <Play className="mr-2 h-3.5 w-3.5 text-blue-400" /> Reactivar plan
          </DropdownMenuItem>
        ) : null}

        {tenant.isDemo && (demoReset || demoReseed) ? (
          <>
            <DropdownMenuSeparator />
            {demoReset ? (
              <DropdownMenuItem onClick={demoReset.onClick}>{demoReset.label}</DropdownMenuItem>
            ) : null}
            {demoReseed ? (
              <DropdownMenuItem onClick={demoReseed.onClick}>{demoReseed.label}</DropdownMenuItem>
            ) : null}
          </>
        ) : null}

        <DropdownMenuSeparator />

        {lifecycle === "active" || lifecycle === "paused" ? (
          <DropdownMenuItem
            className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
            onClick={() => void onSetLifecycle("suspended")}
          >
            <XCircle className="mr-2 h-3.5 w-3.5" /> Suspender workspace
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
          onClick={() => void onSetSubscriptionStatus("suspended")}
        >
          <XCircle className="mr-2 h-3.5 w-3.5" /> Suspender plan
        </DropdownMenuItem>
        {onDelete ? (
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="mr-2 h-4 w-4" /> {tenant.isDemo ? "Eliminar demo" : "Eliminar workspace"}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
